import type { Token } from './tokens';
import { TokenType } from './tokens';
import type { ScadError, SourcePosition } from './errors';
import type {
  Program, Statement, Expression, AssignmentStatement,
  ModuleCallStatement, ModuleDefinitionStatement, NumberLiteral,
  BooleanLiteral, StringLiteral, VectorExpression, IdentifierExpression,
  UnaryExpression, BinaryExpression, FunctionCallExpression,
  RangeExpression, IndexExpression, ForStatement, TernaryExpression,
  IfStatement, FunctionDefinitionStatement, ListCompExpression,
  EachExpression, LetExpression,
} from './ast';

export function parse(tokens: Token[]): { ast: Program; errors: ScadError[] } {
  const errors: ScadError[] = [];
  let pos = 0;

  function current(): Token {
    return tokens[pos];
  }

  function peek(): TokenType {
    return tokens[pos].type;
  }

  function peekAt(offset: number): TokenType {
    const idx = pos + offset;
    return idx < tokens.length ? tokens[idx].type : TokenType.EOF;
  }

  function advance(): Token {
    const tok = tokens[pos];
    if (tok.type !== TokenType.EOF) pos++;
    return tok;
  }

  function expect(type: TokenType): Token {
    if (peek() === type) return advance();
    const tok = current();
    errors.push({
      message: `Expected '${type}' but got '${tok.type}'`,
      from: tok.from,
      to: tok.to,
      severity: 'error',
    });
    return tok;
  }

  function match(type: TokenType): boolean {
    if (peek() === type) {
      advance();
      return true;
    }
    return false;
  }

  function here(): SourcePosition {
    return current().from;
  }

  function skipToRecovery(): void {
    let braceDepth = 0;
    while (peek() !== TokenType.EOF) {
      if (peek() === TokenType.LBrace) {
        braceDepth++;
        advance();
        continue;
      }
      if (peek() === TokenType.RBrace) {
        if (braceDepth > 0) {
          braceDepth--;
          advance();
          continue;
        }
        advance(); // Skip stray closing brace
        return;
      }
      if (peek() === TokenType.Semicolon && braceDepth === 0) {
        advance();
        return;
      }
      advance();
    }
  }

  // --- Expressions ---

  function parseExpression(): Expression {
    return parseTernary();
  }

  function parseTernary(): Expression {
    const expr = parseOr();
    if (peek() === TokenType.QuestionMark) {
      advance(); // consume ?
      const ifExpr = parseExpression();
      expect(TokenType.Colon);
      const elseExpr = parseExpression();
      return { type: 'TernaryExpression', condition: expr, ifExpr, elseExpr, from: expr.from, to: elseExpr.to } as TernaryExpression;
    }
    return expr;
  }

  function parseOr(): Expression {
    let left = parseAnd();
    while (peek() === TokenType.Or) {
      const op = advance();
      const right = parseAnd();
      left = { type: 'BinaryExpression', operator: '||', left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseAnd(): Expression {
    let left = parseEquality();
    while (peek() === TokenType.And) {
      advance();
      const right = parseEquality();
      left = { type: 'BinaryExpression', operator: '&&', left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseEquality(): Expression {
    let left = parseComparison();
    while (peek() === TokenType.EqualEqual || peek() === TokenType.BangEqual) {
      const op = advance().value as '==' | '!=';
      const right = parseComparison();
      left = { type: 'BinaryExpression', operator: op, left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseComparison(): Expression {
    let left = parseAdditive();
    while (peek() === TokenType.Less || peek() === TokenType.Greater ||
           peek() === TokenType.LessEq || peek() === TokenType.GreaterEq) {
      const op = advance().value as '<' | '>' | '<=' | '>=';
      const right = parseAdditive();
      left = { type: 'BinaryExpression', operator: op, left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseAdditive(): Expression {
    let left = parseMultiplicative();
    while (peek() === TokenType.Plus || peek() === TokenType.Minus) {
      const op = advance().value as '+' | '-';
      const right = parseMultiplicative();
      left = { type: 'BinaryExpression', operator: op, left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseMultiplicative(): Expression {
    let left = parseUnary();
    while (peek() === TokenType.Star || peek() === TokenType.Slash || peek() === TokenType.Percent) {
      const op = advance().value as '*' | '/' | '%';
      const right = parseUnary();
      left = { type: 'BinaryExpression', operator: op, left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parseUnary(): Expression {
    if (peek() === TokenType.Minus) {
      const from = here();
      advance();
      const operand = parseUnary();
      return { type: 'UnaryExpression', operator: '-', operand, from, to: operand.to } as UnaryExpression;
    }
    if (peek() === TokenType.Bang) {
      const from = here();
      advance();
      const operand = parseUnary();
      return { type: 'UnaryExpression', operator: '!', operand, from, to: operand.to } as UnaryExpression;
    }
    return parsePower();
  }

  // OpenSCAD 2021.01+: `^` binds tighter than `*` and unary minus (-2^2 == -4),
  // is right-associative (2^3^2 == 2^9), and the exponent may carry its own
  // sign (2^-1 == 0.5).
  function parsePower(): Expression {
    const left = parsePrimary();
    if (peek() === TokenType.Caret) {
      advance();
      const right = parseUnary();
      return { type: 'BinaryExpression', operator: '^', left, right, from: left.from, to: right.to } as BinaryExpression;
    }
    return left;
  }

  function parsePrimary(): Expression {
    const tok = current();
    let expr: Expression;

    // let(a=1, b=2) expr
    if (tok.type === TokenType.Let) {
      const from = here();
      advance(); // consume 'let'
      expect(TokenType.LParen);
      const bindings: { name: string; value: Expression }[] = [];
      while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
        const nameTok = expect(TokenType.Identifier);
        expect(TokenType.Equals);
        const val = parseExpression();
        bindings.push({ name: nameTok.value, value: val });
        if (!match(TokenType.Comma)) break;
      }
      expect(TokenType.RParen);
      const body = parseExpression();
      return { type: 'LetExpression', bindings, body, from, to: body.to } as LetExpression;
    }

    if (tok.type === TokenType.Number) {
      advance();
      expr = { type: 'NumberLiteral', value: parseFloat(tok.value), from: tok.from, to: tok.to } as NumberLiteral;
    } else if (tok.type === TokenType.Boolean) {
      advance();
      expr = { type: 'BooleanLiteral', value: tok.value === 'true', from: tok.from, to: tok.to } as BooleanLiteral;
    } else if (tok.type === TokenType.String) {
      advance();
      expr = { type: 'StringLiteral', value: tok.value, from: tok.from, to: tok.to } as StringLiteral;
    } else if (tok.type === TokenType.Identifier) {
      advance();
      // Function call: identifier followed by (
      if (peek() === TokenType.LParen) {
        expr = parseFunctionCallArgs(tok);
      } else {
        expr = { type: 'IdentifierExpression', name: tok.value, from: tok.from, to: tok.to } as IdentifierExpression;
      }
    } else if (tok.type === TokenType.Each) {
      // `each expr` — usable inside list comprehension bodies and vector elements
      const from = here();
      advance(); // consume 'each'
      const inner = parseExpression();
      return { type: 'EachExpression', expr: inner, from, to: inner.to } as EachExpression;
    } else if (tok.type === TokenType.LBracket) {
      expr = parseVectorOrRange();
    } else if (tok.type === TokenType.LParen) {
      advance();
      expr = parseExpression();
      expect(TokenType.RParen);
    } else {
      // Error recovery
      errors.push({
        message: `Unexpected token '${tok.value || tok.type}'`,
        from: tok.from,
        to: tok.to,
        severity: 'error',
      });
      advance();
      return { type: 'NumberLiteral', value: 0, from: tok.from, to: tok.to } as NumberLiteral;
    }

    // Postfix index: expr[index] or dot access: expr.x, expr.y, expr.z
    while (peek() === TokenType.LBracket || peek() === TokenType.Dot) {
      if (peek() === TokenType.LBracket) {
        advance(); // consume [
        const index = parseExpression();
        const endTok = current();
        expect(TokenType.RBracket);
        expr = { type: 'IndexExpression', object: expr, index, from: expr.from, to: endTok.to } as IndexExpression;
      } else {
        advance(); // consume .
        const memberTok = current();
        if (peek() !== TokenType.Identifier) {
          errors.push({ message: `Expected property name after '.'`, from: memberTok.from, to: memberTok.to, severity: 'error' });
          break;
        }
        const propTok = advance();
        const propMap: Record<string, number> = { x: 0, y: 1, z: 2 };
        const idx = propMap[propTok.value];
        if (idx === undefined) {
          errors.push({ message: `Unknown vector property '${propTok.value}' (use .x, .y, or .z)`, from: propTok.from, to: propTok.to, severity: 'error' });
          break;
        }
        const index: NumberLiteral = { type: 'NumberLiteral', value: idx, from: propTok.from, to: propTok.to };
        expr = { type: 'IndexExpression', object: expr, index, from: expr.from, to: propTok.to } as IndexExpression;
      }
    }

    return expr;
  }

  function parseFunctionCallArgs(nameTok: Token): FunctionCallExpression {
    advance(); // consume (
    const args: { name?: string; value: Expression }[] = [];
    while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
      // Check for named argument: identifier = expr
      if (peek() === TokenType.Identifier && peekAt(1) === TokenType.Equals) {
        const name = advance().value;
        advance(); // =
        const value = parseExpression();
        args.push({ name, value });
      } else {
        args.push({ value: parseExpression() });
      }
      if (!match(TokenType.Comma)) break;
    }
    const endTok = current();
    expect(TokenType.RParen);
    return {
      type: 'FunctionCallExpression',
      name: nameTok.value,
      args,
      from: nameTok.from,
      to: endTok.to,
    };
  }

  function parseVectorOrRange(): VectorExpression | RangeExpression | ListCompExpression {
    const from = here();
    advance(); // consume [

    if (peek() === TokenType.RBracket) {
      const endTok = current();
      advance();
      return { type: 'VectorExpression', elements: [], from, to: endTok.to };
    }

    // List comprehension: [for (var = range) [if (cond)] body]
    if (peek() === TokenType.For) {
      advance(); // consume 'for'
      expect(TokenType.LParen);
      const varTok = expect(TokenType.Identifier);
      expect(TokenType.Equals);
      const range = parseExpression();
      expect(TokenType.RParen);
      // Optional if-filter
      let condition: Expression | undefined;
      if (peek() === TokenType.If) {
        advance(); // consume 'if'
        expect(TokenType.LParen);
        condition = parseExpression();
        expect(TokenType.RParen);
      }
      const body = parseExpression();
      const endTok = current();
      expect(TokenType.RBracket);
      return {
        type: 'ListCompExpression',
        variable: varTok.value,
        range,
        body,
        condition,
        from,
        to: endTok.to,
      } as ListCompExpression;
    }

    // Conditional vector: [ if (cond) expr ] → cond ? [expr] : []
    if (peek() === TokenType.If) {
      advance(); // consume 'if'
      expect(TokenType.LParen);
      const condition = parseExpression();
      expect(TokenType.RParen);
      const body = parseExpression();
      const endTok = current();
      expect(TokenType.RBracket);
      const emptyVec: VectorExpression = { type: 'VectorExpression', elements: [], from, to: endTok.to };
      const singleVec: VectorExpression = { type: 'VectorExpression', elements: [body], from, to: endTok.to };
      return {
        type: 'TernaryExpression',
        condition,
        ifExpr: singleVec,
        elseExpr: emptyVec,
        from,
        to: endTok.to,
      } as unknown as VectorExpression; // Fits in Expression union; callers accept Expression
    }

    // Parse first element (may be `each expr`)
    const first = parseVectorElement();

    // Check for range: [start : end] or [start : step : end]
    if (first.type !== 'EachExpression' && peek() === TokenType.Colon) {
      advance(); // consume first :
      const second = parseExpression();
      if (peek() === TokenType.Colon) {
        advance(); // consume second :
        const third = parseExpression();
        const endTok = current();
        expect(TokenType.RBracket);
        return { type: 'RangeExpression', start: first, end: third, step: second, from, to: endTok.to };
      }
      const endTok = current();
      expect(TokenType.RBracket);
      return { type: 'RangeExpression', start: first, end: second, from, to: endTok.to };
    }

    // Regular vector
    const elements: Expression[] = [first];
    while (match(TokenType.Comma)) {
      if (peek() === TokenType.RBracket) break;
      elements.push(parseVectorElement());
    }
    const endTok = current();
    expect(TokenType.RBracket);
    return { type: 'VectorExpression', elements, from, to: endTok.to };
  }

  function parseVectorElement(): Expression {
    // Handle `each expr` inside vectors
    if (peek() === TokenType.Each) {
      const from = here();
      advance(); // consume 'each'
      const expr = parseExpression();
      return { type: 'EachExpression', expr, from, to: expr.to } as EachExpression;
    }
    return parseExpression();
  }

  // --- Statements ---

  function parseForStatement(): ForStatement {
    const from = here();
    advance(); // consume 'for'
    expect(TokenType.LParen);

    // Collect one or more variable = range assignments (comma-separated)
    const assignments: { variable: string; range: Expression }[] = [];
    const varTok = expect(TokenType.Identifier);
    expect(TokenType.Equals);
    const range = parseExpression();
    assignments.push({ variable: varTok.value, range });

    while (peek() === TokenType.Comma) {
      advance(); // consume ','
      const v = expect(TokenType.Identifier);
      expect(TokenType.Equals);
      const r = parseExpression();
      assignments.push({ variable: v.value, range: r });
    }

    expect(TokenType.RParen);

    const body: Statement[] = [];
    if (peek() === TokenType.LBrace) {
      advance(); // {
      while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
        const stmt = parseStatement();
        if (stmt) body.push(stmt);
      }
      expect(TokenType.RBrace);
    } else {
      // Single statement body (including chained for)
      const stmt = parseStatement();
      if (stmt) body.push(stmt);
    }

    // Build nested ForStatements for multi-variable: for(x=[..], y=[..]) → for(x) { for(y) { body } }
    let result: ForStatement = {
      type: 'ForStatement',
      variable: assignments[assignments.length - 1].variable,
      range: assignments[assignments.length - 1].range,
      body,
      from,
      to: here(),
    };

    for (let i = assignments.length - 2; i >= 0; i--) {
      result = {
        type: 'ForStatement',
        variable: assignments[i].variable,
        range: assignments[i].range,
        body: [result],
        from,
        to: here(),
      };
    }

    return result;
  }

  function parseIfStatement(): IfStatement {
    const from = here();
    advance(); // consume 'if'
    expect(TokenType.LParen);
    const condition = parseExpression();
    expect(TokenType.RParen);

    const thenBody: Statement[] = [];
    if (peek() === TokenType.LBrace) {
      advance(); // {
      while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
        const stmt = parseStatement();
        if (stmt) thenBody.push(stmt);
      }
      expect(TokenType.RBrace);
    } else {
      const stmt = parseStatement();
      if (stmt) thenBody.push(stmt);
    }

    const elseBody: Statement[] = [];
    if (peek() === TokenType.Else) {
      advance(); // consume 'else'
      if (peek() === TokenType.If) {
        // else if — chain as single statement in elseBody
        elseBody.push(parseIfStatement());
      } else if (peek() === TokenType.LBrace) {
        advance(); // {
        while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
          const stmt = parseStatement();
          if (stmt) elseBody.push(stmt);
        }
        expect(TokenType.RBrace);
      } else {
        const stmt = parseStatement();
        if (stmt) elseBody.push(stmt);
      }
    }

    return {
      type: 'IfStatement',
      condition,
      thenBody,
      elseBody,
      from,
      to: here(),
    };
  }

  function parseStatement(): Statement | null {
    if (peek() === TokenType.EOF) return null;
    const startPos = pos; // Track position for stall detection

    // Skip stray semicolons
    if (peek() === TokenType.Semicolon) {
      advance();
      return null;
    }

    // Debug modifier: # before a statement
    if (peek() === TokenType.Hash) {
      advance(); // consume #
      const stmt = parseStatement();
      if (stmt && (stmt.type === 'ModuleCallStatement' || stmt.type === 'ForStatement')) {
        stmt.debug = true;
      }
      return stmt;
    }

    // Background modifier: % before a statement
    if (peek() === TokenType.Percent) {
      advance(); // consume %
      const stmt = parseStatement();
      if (stmt && (stmt.type === 'ModuleCallStatement' || stmt.type === 'ForStatement')) {
        stmt.background = true;
      }
      return stmt;
    }

    // Root modifier: ! before a statement
    if (peek() === TokenType.Bang) {
      advance(); // consume !
      const stmt = parseStatement();
      if (stmt && (stmt.type === 'ModuleCallStatement' || stmt.type === 'ForStatement')) {
        stmt.root = true;
      }
      return stmt;
    }

    // Disable modifier: * before a statement — skip entirely
    if (peek() === TokenType.Star) {
      advance(); // consume *
      parseStatement(); // parse and discard
      return null;
    }

    // For loop
    if (peek() === TokenType.For) {
      return parseForStatement();
    }

    // If statement
    if (peek() === TokenType.If) {
      return parseIfStatement();
    }

    // let() as statement: let(bindings) statement
    if (peek() === TokenType.Let) {
      return parseLetStatement();
    }

    // function declaration: function name(params) = expr;
    if (peek() === TokenType.Function) {
      return parseFunctionDefinition();
    }

    if (peek() === TokenType.Identifier) {
      // Module definition: module name(params) { body }
      if (current().value === 'module') {
        return parseModuleDefinition();
      }
      // include/use directives: include <path> ; or use <path> ;
      if (current().value === 'include' || current().value === 'use') {
        advance(); // consume 'include' or 'use'
        if (peek() === TokenType.Less) {
          advance(); // consume '<'
          // Read tokens until '>' to build the path string
          while (peek() !== TokenType.Greater && peek() !== TokenType.EOF) {
            advance();
          }
          if (peek() === TokenType.Greater) advance(); // consume '>'
          match(TokenType.Semicolon); // optional trailing semicolon
          return null; // no-op — actual resolution happens at app layer
        }
        // If no '<', fall through to try as a regular module call
        // (rewind not possible, so just skip to recovery)
        skipToRecovery();
        return null;
      }
      // Assignment: identifier = expr ;
      if (peekAt(1) === TokenType.Equals) {
        return parseAssignment();
      }
      // Module call: identifier ( args ) { children } | ;
      return parseModuleCall();
    }

    // Error recovery
    const tok = current();
    errors.push({
      message: `Unexpected token '${tok.value || tok.type}'`,
      from: tok.from,
      to: tok.to,
      severity: 'error',
    });
    skipToRecovery();
    // Safety: ensure we always advance past the problematic token
    if (pos === startPos && peek() !== TokenType.EOF) advance();
    return null;
  }

  function parseAssignment(): AssignmentStatement {
    const nameTok = advance();
    advance(); // consume =
    const value = parseExpression();
    expect(TokenType.Semicolon);
    return {
      type: 'AssignmentStatement',
      name: nameTok.value,
      value,
      from: nameTok.from,
      to: value.to,
    };
  }

  function parseModuleCall(): ModuleCallStatement {
    const nameTok = advance();
    expect(TokenType.LParen);
    const args: { name?: string; value: Expression }[] = [];
    while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
      if (peek() === TokenType.Identifier && peekAt(1) === TokenType.Equals) {
        const name = advance().value;
        advance(); // =
        const value = parseExpression();
        args.push({ name, value });
      } else {
        args.push({ value: parseExpression() });
      }
      if (!match(TokenType.Comma)) break;
    }
    expect(TokenType.RParen);

    // Children
    const children: Statement[] = [];
    if (peek() === TokenType.LBrace) {
      advance(); // {
      while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
        const stmt = parseStatement();
        if (stmt) children.push(stmt);
      }
      expect(TokenType.RBrace);
    } else if (peek() === TokenType.Semicolon) {
      advance();
    } else {
      // Single child statement (e.g., translate([1,0,0]) cube(1);)
      const child = parseStatement();
      if (child) children.push(child);
    }

    return {
      type: 'ModuleCallStatement',
      name: nameTok.value,
      args,
      children,
      from: nameTok.from,
      to: current().from,
    };
  }

  function parseModuleDefinition(): ModuleDefinitionStatement {
    const from = here();
    advance(); // consume 'module'
    const nameTok = expect(TokenType.Identifier);
    expect(TokenType.LParen);

    const params: { name: string; defaultValue?: Expression }[] = [];
    while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
      const paramTok = expect(TokenType.Identifier);
      let defaultValue: Expression | undefined;
      if (match(TokenType.Equals)) {
        defaultValue = parseExpression();
      }
      params.push({ name: paramTok.value, defaultValue });
      if (!match(TokenType.Comma)) break;
    }
    expect(TokenType.RParen);

    expect(TokenType.LBrace);
    const body: Statement[] = [];
    while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
      const stmt = parseStatement();
      if (stmt) body.push(stmt);
    }
    expect(TokenType.RBrace);

    return {
      type: 'ModuleDefinitionStatement',
      name: nameTok.value,
      params,
      body,
      from,
      to: here(),
    };
  }

  function parseFunctionDefinition(): FunctionDefinitionStatement {
    const from = here();
    advance(); // consume 'function'
    const nameTok = expect(TokenType.Identifier);
    expect(TokenType.LParen);

    const params: { name: string; defaultValue?: Expression }[] = [];
    while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
      const paramTok = expect(TokenType.Identifier);
      let defaultValue: Expression | undefined;
      if (match(TokenType.Equals)) {
        defaultValue = parseExpression();
      }
      params.push({ name: paramTok.value, defaultValue });
      if (!match(TokenType.Comma)) break;
    }
    expect(TokenType.RParen);

    expect(TokenType.Equals);
    const body = parseExpression();
    expect(TokenType.Semicolon);

    return {
      type: 'FunctionDefinitionStatement',
      name: nameTok.value,
      params,
      body,
      from,
      to: here(),
    };
  }

  function parseLetStatement(): Statement | null {
    const from = here();
    advance(); // consume 'let'
    expect(TokenType.LParen);
    const bindings: { name: string; value: Expression }[] = [];
    while (peek() !== TokenType.RParen && peek() !== TokenType.EOF) {
      const nameTok = expect(TokenType.Identifier);
      expect(TokenType.Equals);
      const val = parseExpression();
      bindings.push({ name: nameTok.value, value: val });
      if (!match(TokenType.Comma)) break;
    }
    expect(TokenType.RParen);

    // Parse the body statement(s)
    const body: Statement[] = [];
    if (peek() === TokenType.LBrace) {
      advance(); // {
      while (peek() !== TokenType.RBrace && peek() !== TokenType.EOF) {
        const stmt = parseStatement();
        if (stmt) body.push(stmt);
      }
      expect(TokenType.RBrace);
    } else {
      const stmt = parseStatement();
      if (stmt) body.push(stmt);
    }

    // Desugar: create assignment statements + body statements wrapped in a module call
    // We'll desugar let(a=1, b=2) stmt into: assignments followed by body
    // Since we need to return a single statement, wrap in a group-like structure
    // Actually, simplest approach: produce assignment statements + the body statement(s)
    // But we can only return one statement. Let's produce a synthetic module call to "union" with no args.
    const allStmts: Statement[] = [];
    for (const b of bindings) {
      allStmts.push({
        type: 'AssignmentStatement',
        name: b.name,
        value: b.value,
        from,
        to: from,
      });
    }
    allStmts.push(...body);

    // If only one body statement and no bindings, just return it
    if (allStmts.length === 1) return allStmts[0];

    // Wrap in a union() to group them
    return {
      type: 'ModuleCallStatement',
      name: 'union',
      args: [],
      children: allStmts,
      from,
      to: here(),
    } as ModuleCallStatement;
  }

  // --- Program ---

  const from = here();
  const statements: Statement[] = [];
  while (peek() !== TokenType.EOF) {
    const prevPos = pos;
    const stmt = parseStatement();
    if (stmt) statements.push(stmt);
    // Safety: if parser didn't advance, force skip to prevent infinite loop
    if (pos === prevPos) {
      errors.push({
        message: `Parser stalled at '${current().value || current().type}'`,
        from: current().from,
        to: current().to,
        severity: 'error',
      });
      advance();
    }
  }

  return {
    ast: { type: 'Program', statements, from, to: here() },
    errors,
  };
}
